import MagnifyingGlassMinusIcon from '@heroicons/react/24/outline/MagnifyingGlassMinusIcon';
import MagnifyingGlassPlusIcon from '@heroicons/react/24/outline/MagnifyingGlassPlusIcon';
import { PointerEvent, useRef, useState } from 'react';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { useTranslation } from '@/hooks/useTranslation';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

interface DamImageLightboxProps {
    imageUrl: string;
    title: string;
    onClose: () => void;
}

/**
 * Fullscreen view of the preview (Make lightbox): the image fills the view, also when it is
 * smaller; zoom from 0.5 to 3 in steps of 0.25, drag to pan, the file name as title; Escape
 * and the close button close it.
 */
function DamImageLightbox({ imageUrl, title, onClose }: DamImageLightboxProps) {
    const { t } = useTranslation();
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragStart = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);

    const changeZoom = (delta: number) => setZoom((current) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current + delta)));

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStart.current = { x: event.clientX, y: event.clientY, offsetX: offset.x, offsetY: offset.y };
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const start = dragStart.current;
        if (start) {
            setOffset({ x: start.offsetX + event.clientX - start.x, y: start.offsetY + event.clientY - start.y });
        }
    };

    const handlePointerUp = () => {
        dragStart.current = null;
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent variant="screen">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>{title}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody padding="none" className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-overflow-hidden tw-bg-fill-secondary">
                    <div
                        className="tw-flex tw-h-full tw-w-full tw-cursor-grab tw-touch-none tw-items-center tw-justify-center active:tw-cursor-grabbing"
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                    >
                        <img
                            src={imageUrl}
                            alt={title}
                            draggable={false}
                            className="tw-h-full tw-w-full tw-select-none tw-object-contain"
                            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})` }}
                        />
                    </div>
                    <div className="tw-absolute tw-bottom-4 tw-right-4 tw-flex tw-gap-2">
                        <ButtonPanel
                            isDisabled={zoom <= MIN_ZOOM}
                            icon={MagnifyingGlassMinusIcon}
                            tooltip={t('DAM.DETAIL.ZOOM_OUT')}
                            aria-label={t('DAM.DETAIL.ZOOM_OUT')}
                            onClick={() => changeZoom(-ZOOM_STEP)}
                            className="tw-bg-fill-primary"
                        />
                        <ButtonPanel
                            isDisabled={zoom >= MAX_ZOOM}
                            icon={MagnifyingGlassPlusIcon}
                            tooltip={t('DAM.DETAIL.ZOOM_IN')}
                            aria-label={t('DAM.DETAIL.ZOOM_IN')}
                            onClick={() => changeZoom(ZOOM_STEP)}
                            className="tw-bg-fill-primary"
                        />
                    </div>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
}

export { DamImageLightbox };
export type { DamImageLightboxProps };
